import Dexie, { type EntityTable, type InsertType } from 'dexie';

// Database schema interfaces matching the current D1 schema
export interface User {
  id?: number;
  email: string;
  name?: string;
  avatar?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Session {
  id?: number;
  userId: number;
  token: string;
  expiresAt: Date;
  createdAt: Date;
}

export interface Project {
  id?: number;
  userId: number;
  agentId: string;
  title: string;
  description?: string;
  blueprint?: string; // JSON string
  status: 'active' | 'completed' | 'archived';
  createdAt: Date;
  updatedAt: Date;
}

export interface CodeGeneration {
  id?: number;
  projectId: number;
  phase: string;
  files: string; // JSON string of FileType[]
  status: 'generating' | 'completed' | 'error';
  createdAt: Date;
  updatedAt: Date;
}

export interface Template {
  id?: number;
  name: string;
  description?: string;
  files: string; // JSON string
  category: string;
  createdAt: Date;
}

export interface UserPreference {
  id?: number;
  userId: number;
  key: string;
  value: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface FavoriteApp {
  id?: number;
  userId: number;
  projectId: number;
  createdAt: Date;
}

// IndexedDB database class
class AppBuilderDB extends Dexie {
  users!: EntityTable<User, 'id'>;
  sessions!: EntityTable<Session, 'id'>;
  projects!: EntityTable<Project, 'id'>;
  codeGenerations!: EntityTable<CodeGeneration, 'id'>;
  templates!: EntityTable<Template, 'id'>;
  userPreferences!: EntityTable<UserPreference, 'id'>;
  favoriteApps!: EntityTable<FavoriteApp, 'id'>;

  constructor() {
    super('AppBuilderDB');

    this.version(1).stores({
      users: '++id, email, createdAt',
      sessions: '++id, userId, token, expiresAt',
      projects: '++id, userId, agentId, status, createdAt, updatedAt',
      codeGenerations: '++id, projectId, phase, status, createdAt',
      templates: '++id, name, category, createdAt',
      userPreferences: '++id, userId, key',
      favoriteApps: '++id, userId, projectId, createdAt'
    });

    // Add hooks for automatic timestamps
    this.users.hook('creating', (_primKey, obj, _trans) => {
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
    });

    this.users.hook('updating', (modifications) => {
      (modifications as Partial<User>).updatedAt = new Date();
    });

    this.sessions.hook('creating', (_primKey, obj, _trans) => {
      obj.createdAt = new Date();
    });

    this.projects.hook('creating', (_primKey, obj, _trans) => {
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
    });

    this.projects.hook('updating', (modifications) => {
      (modifications as Partial<Project>).updatedAt = new Date();
    });

    this.codeGenerations.hook('creating', (_primKey, obj, _trans) => {
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
    });

    this.codeGenerations.hook('updating', (modifications) => {
      (modifications as Partial<CodeGeneration>).updatedAt = new Date();
    });

    this.templates.hook('creating', (_primKey, obj, _trans) => {
      obj.createdAt = new Date();
    });

    this.userPreferences.hook('creating', (_primKey, obj, _trans) => {
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
    });

    this.userPreferences.hook('updating', (modifications) => {
      (modifications as Partial<UserPreference>).updatedAt = new Date();
    });

    // FavoriteApps hooks
    this.favoriteApps.hook('creating', (_primKey, obj, _trans) => {
      obj.createdAt = new Date();
    });
  }
}

// Create database instance
export const db = new AppBuilderDB();

// Database service class with methods matching the current D1 service
export class BrowserDatabaseService {
  // Database connection
  async open(): Promise<void> {
    await db.open();
  }

  // User operations
  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    const id = await db.users.add(userData as unknown as InsertType<User, 'id'>);
    return (await db.users.get(id))!;
  }

  async getUserByEmail(email: string): Promise<User | undefined> {
    return await db.users.where('email').equals(email).first();
  }

  async getUserById(id: number): Promise<User | undefined> {
    return await db.users.get(id);
  }

  async updateUser(id: number, updates: Partial<User>): Promise<void> {
    await db.users.update(id, updates);
  }

  // Session operations
  async createSession(sessionData: Omit<Session, 'id' | 'createdAt'>): Promise<Session> {
    const id = await db.sessions.add(sessionData as unknown as InsertType<Session, 'id'>);
    return (await db.sessions.get(id))!;
  }

  async getSessionByToken(token: string): Promise<Session | undefined> {
    return await db.sessions.where('token').equals(token).first();
  }

  async deleteSession(id: number): Promise<void> {
    await db.sessions.delete(id);
  }

  async deleteExpiredSessions(): Promise<void> {
    const now = new Date();
    await db.sessions.where('expiresAt').below(now).delete();
  }

  // Project operations
  async createProject(projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<Project> {
    const id = await db.projects.add(projectData as unknown as InsertType<Project, 'id'>);
    return (await db.projects.get(id))!;
  }

  async getProjectById(id: number): Promise<Project | undefined> {
    return await db.projects.get(id);
  }

  async getProjectByAgentId(agentId: string): Promise<Project | undefined> {
    console.log('[browserDB.getProjectByAgentId] ENTRY, agentId:', agentId);
    const result = await db.projects.where('agentId').equals(agentId).first();
    console.log('[browserDB.getProjectByAgentId] Result:', result ? `Found project ID ${result.id}` : 'Not found');
    return result;
  }

  async getProjectsByUserId(userId: number): Promise<Project[]> {
    return await db.projects.where('userId').equals(userId).toArray();
  }

  async updateProject(id: number, updates: Partial<Project>): Promise<void> {
    await db.projects.update(id, updates);
  }

  async deleteProject(id: number): Promise<void> {
    await db.projects.delete(id);
  }

  // Additional project methods for CSR compatibility
  async getUserProjects(userId: number): Promise<Project[]> {
    return await this.getProjectsByUserId(userId);
  }

  async getProject(projectId: string): Promise<Project | undefined> {
    // Try to parse as number first, then search by agentId if that fails
    const numericId = parseInt(projectId, 10);
    if (!isNaN(numericId)) {
      return await this.getProjectById(numericId);
    }
    return await this.getProjectByAgentId(projectId);
  }

  // Code generation operations
  async createCodeGeneration(codeGenData: Omit<CodeGeneration, 'id' | 'createdAt' | 'updatedAt'>): Promise<CodeGeneration> {
    const id = await db.codeGenerations.add(codeGenData as unknown as InsertType<CodeGeneration, 'id'>);
    return (await db.codeGenerations.get(id))!;
  }

  async getCodeGenerationsByProjectId(projectId: number): Promise<CodeGeneration[]> {
    return await db.codeGenerations.where('projectId').equals(projectId).toArray();
  }

  async updateCodeGeneration(id: number, updates: Partial<CodeGeneration>): Promise<void> {
    await db.codeGenerations.update(id, updates);
  }

  async getLatestCodeGeneration(projectId: number): Promise<CodeGeneration | undefined> {
    const items = await db.codeGenerations
      .where('projectId')
      .equals(projectId)
      .toArray();
    if (items.length === 0) return undefined;
    items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return items[0];
  }

  // Template operations
  async createTemplate(templateData: Omit<Template, 'id' | 'createdAt'>): Promise<Template> {
    const id = await db.templates.add(templateData as unknown as InsertType<Template, 'id'>);
    return (await db.templates.get(id))!;
  }

  async getTemplates(): Promise<Template[]> {
    return await db.templates.toArray();
  }

  async getTemplatesByCategory(category: string): Promise<Template[]> {
    return await db.templates.where('category').equals(category).toArray();
  }

  // User preferences operations
  async setUserPreference(userId: number, key: string, value: string): Promise<void> {
    const existing = await db.userPreferences.where(['userId', 'key']).equals([userId, key]).first();
    if (existing) {
      await db.userPreferences.update(existing.id!, { value });
    } else {
      await db.userPreferences.add({ userId, key, value } as unknown as InsertType<UserPreference, 'id'>);
    }
  }

  async getUserPreference(userId: number, key: string): Promise<string | undefined> {
    const pref = await db.userPreferences.where(['userId', 'key']).equals([userId, key]).first();
    return pref?.value;
  }

  async getUserPreferences(userId: number): Promise<Record<string, string>> {
    const prefs = await db.userPreferences.where('userId').equals(userId).toArray();
    return prefs.reduce((acc, pref) => {
      acc[pref.key] = pref.value;
      return acc;
    }, {} as Record<string, string>);
  }

  // Favorite app operations
  async addFavoriteApp(userId: number, projectId: number): Promise<void> {
    const existing = await db.favoriteApps.where(['userId', 'projectId']).equals([userId, projectId]).first();
    if (!existing) {
      await db.favoriteApps.add({ userId, projectId, createdAt: new Date() });
    }
  }

  async removeFavoriteApp(userId: number, projectId: number): Promise<void> {
    await db.favoriteApps.where(['userId', 'projectId']).equals([userId, projectId]).delete();
  }

  async isAppStarred(userId: number, projectId: string): Promise<boolean> {
    // Try to parse as number first, then search by agentId if that fails
    const numericId = parseInt(projectId, 10);
    let actualProjectId: number;

    if (!isNaN(numericId)) {
      actualProjectId = numericId;
    } else {
      const project = await this.getProjectByAgentId(projectId);
      if (!project?.id) return false;
      actualProjectId = project.id;
    }

    const favorite = await db.favoriteApps.where(['userId', 'projectId']).equals([userId, actualProjectId]).first();
    return !!favorite;
  }

  async getFavoriteApps(userId: number): Promise<Project[]> {
    const favorites = await db.favoriteApps.where('userId').equals(userId).toArray();
    const projectIds = favorites.map(f => f.projectId);

    if (projectIds.length === 0) return [];

    const projects = await db.projects.where('id').anyOf(projectIds).toArray();
    return projects;
  }

  // Utility operations
  async clearAllData(): Promise<void> {
    await db.transaction('rw', [db.users, db.sessions, db.projects, db.codeGenerations, db.templates, db.userPreferences, db.favoriteApps], async () => {
      await db.users.clear();
      await db.sessions.clear();
      await db.projects.clear();
      await db.codeGenerations.clear();
      await db.templates.clear();
      await db.userPreferences.clear();
      await db.favoriteApps.clear();
    });
  }

  async exportData(): Promise<string> {
    const data = {
      users: await db.users.toArray(),
      sessions: await db.sessions.toArray(),
      projects: await db.projects.toArray(),
      codeGenerations: await db.codeGenerations.toArray(),
      templates: await db.templates.toArray(),
      userPreferences: await db.userPreferences.toArray(),
      favoriteApps: await db.favoriteApps.toArray(),
      exportedAt: new Date().toISOString()
    };
    return JSON.stringify(data, null, 2);
  }

  async importData(jsonData: string): Promise<void> {
    const data = JSON.parse(jsonData);
    await db.transaction('rw', [db.users, db.sessions, db.projects, db.codeGenerations, db.templates, db.userPreferences, db.favoriteApps], async () => {
      if (data.users) await db.users.bulkAdd(data.users);
      if (data.sessions) await db.sessions.bulkAdd(data.sessions);
      if (data.projects) await db.projects.bulkAdd(data.projects);
      if (data.codeGenerations) await db.codeGenerations.bulkAdd(data.codeGenerations);
      if (data.templates) await db.templates.bulkAdd(data.templates);
      if (data.userPreferences) await db.userPreferences.bulkAdd(data.userPreferences);
      if (data.favoriteApps) await db.favoriteApps.bulkAdd(data.favoriteApps);
    });
  }

  async getHealthStatus(): Promise<{ healthy: boolean; timestamp: string }> {
    try {
      await db.users.limit(1).toArray();
      return {
        healthy: true,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        healthy: false,
        timestamp: new Date().toISOString()
      };
    }
  }
}

// Export singleton instance
export const browserDB = new BrowserDatabaseService();
